import React from 'react';
import ZillowInsuranceServicesLogo from '@common/components/ZillowInsuranceServicesLogo';
import { getDate } from 'date-fns';
import Container from '@zg-rentals/multibrand-ui/lib/Container';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Label from '@zg-rentals/multibrand-ui/lib/Label';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import Checkbox from '@zg-rentals/multibrand-ui/lib/Checkbox';
import SpacerRow from '@zg-rentals/multibrand-ui/lib/SpacerRow';
import Or from '@common/components/Or';
import FaqComponent from '@common/components/FaqComponent';
import Modal from '@common/components/Modal';
import SpinnerContainer from '@common/components/SpinnerContainer';
import {
  DISPLAY_DATE_FORMAT,
  makeEndDatesForUpdateTerm,
  STORE_DATE_FORMAT,
} from '@common/paymentsUtils/selectOptionsBuilder';
import userPersonas from '@common/constants/userPersonas';
import FaqConstants from '@rental-manager/constants/FaqConstants';
import { ACTIVE, PENDING_ACTIVATION } from '@rental-manager/constants/RentalProtectionPolicyStatusConstants';
import ConfirmChangeEndDate from './modules/ConfirmChangeEndDate';
import ConfirmMonthToMonth from './modules/ConfirmMonthToMonth';
import ConfirmStopPayments from './modules/ConfirmStopPayments';
import HelpToolTip from '../../Onboarding/HelpToolTip';
import Section from '@rental-manager/components/Section';
import { coerceToDate, formatDate } from '@common/paymentsUtils/dateUtils';
import type { EndDatesInterface } from '@common/types';
import DebouncedButton from '@common/components/DebouncedButton';

const formatEndDate = (date) => formatDate(date, 'MMMM d, yyyy');

interface Props {
  firstDueDate: string;
  paymentDateForFirstMonth: string;
  monthToMonth: boolean;
  requestEditPaymentLength: (...args: Array<any>) => any;
  requestStopPayments: (rentTermsKey) => any;
  onClose: () => void;
  today: Date;
  rentTermsKey?: string;
  rentalProtectionPolicyStatus: string;
  listingAlias: string;
  numberOfPayments: number;
}

interface State {
  startDate: Date;
  endDates: Array<EndDatesInterface>;
  monthToMonth: boolean;
  selectedEndDate: EndDatesInterface;
  paymentEndDateFormatted: string;
  showConfirmChangeEndDate: boolean;
  showConfirmMonthToMonth: boolean;
  showConfirmStopPayments: boolean;
  isLoading: boolean;
}

class EditPaymentLengthModal extends React.Component<Props, State> {
  static defaultProps = {
    firstDueDate: '',
    monthToMonth: false,
    requestEditPaymentLength: () => {},
    requestStopPayments: () => {},
    onClose: () => {},
    today: new Date(),
  };

  constructor(props) {
    super(props);
    const { firstDueDate, monthToMonth, numberOfPayments, today, paymentDateForFirstMonth } = props;
    const parsedFirstDue = coerceToDate(firstDueDate);
    const dayOfTheMonth = parsedFirstDue ? getDate(parsedFirstDue) : 1;
    const endDates = makeEndDatesForUpdateTerm({
      today,
      dueDay: dayOfTheMonth,
      firstDueDateString: firstDueDate,
      currentNumberOfPayments: numberOfPayments,
      monthToMonth,
    });
    const [selectedEndDate] = endDates;

    this.state = {
      startDate: paymentDateForFirstMonth,
      endDates,
      monthToMonth,
      selectedEndDate,
      paymentEndDateFormatted: formatEndDate(selectedEndDate.date),
      showConfirmChangeEndDate: false,
      showConfirmMonthToMonth: false,
      showConfirmStopPayments: false,
      isLoading: false,
    };
  }

  onUpdate = (): void => {
    if (!this.props.monthToMonth && this.state.monthToMonth) {
      this.setState({
        showConfirmMonthToMonth: true,
      });
    } else {
      this.setState({
        showConfirmChangeEndDate: true,
      });
    }

    // TODO: No change: show error
  };

  /* End dates */

  onConfirmChangeEndDate = (): Promise<void> | boolean =>
    Promise.resolve()
      .then(() => this.setState({ isLoading: true }))
      .then(() => {
        const { paymentEndDateFormatted, selectedEndDate } = this.state;
        return this.props.requestEditPaymentLength({
          numberOfPayments: Number(selectedEndDate.paymentsToAdd),
          endDate: paymentEndDateFormatted,
          rentTermsKey: this.props.rentTermsKey,
        });
      })
      .then(({ err } = {}) => {
        if (err) {
          this.setState({ isLoading: false });
        }
      });

  onCloseChangeEndDate = () => Promise.resolve().then(() => this.setState({ showConfirmChangeEndDate: false }));

  /* Month to month */

  onConfirmMonthToMonth = (): Promise<void> | boolean =>
    Promise.resolve()
      .then(() => this.setState({ isLoading: true }))
      .then(() => {
        const { paymentEndDateFormatted } = this.state;

        return this.props.requestEditPaymentLength({
          monthToMonth: true,
          endDate: paymentEndDateFormatted,
          rentTermsKey: this.props.rentTermsKey,
        });
      })
      .then(({ err } = {}) => {
        if (err) {
          this.setState({ isLoading: false });
        }
      });

  onCloseMonthToMonth = () => Promise.resolve().then(() => this.setState({ showConfirmMonthToMonth: false }));

  /* Stop Payments */

  onShowConfirmStopPayments = (): void => {
    this.setState({
      showConfirmStopPayments: true,
    });
  };

  onConfirmStopPayments = (): Promise<void> | boolean =>
    Promise.resolve()
      .then(() => this.setState({ isLoading: true }))
      .then(() => this.props.requestStopPayments(this.props.rentTermsKey))
      .then(({ err } = {}) => {
        if (err) {
          this.setState({ isLoading: false });
        }
      });

  onCloseStopPayments = () => Promise.resolve().then(() => this.setState({ showConfirmStopPayments: false }));

  /* ********** */

  onClose = (): void => {
    this.props.onClose();
  };

  onChangeMonthToMonth = (): void => {
    this.setState({
      monthToMonth: !this.state.monthToMonth,
    });
  };

  onChangeEndDate = (e): void => {
    const { endDates } = this.state;
    const selectedEndDate = endDates[e.currentTarget.selectedIndex];

    this.setState({
      paymentEndDateFormatted: formatEndDate(selectedEndDate.date),
      selectedEndDate,
    });
  };

  renderStartDate = (): React.ReactNode => {
    const { startDate } = this.state;
    const date = formatDate(startDate, STORE_DATE_FORMAT);
    const display = formatDate(startDate, DISPLAY_DATE_FORMAT);

    return (
      <Select disabled>
        <option key={date}>{display}</option>
      </Select>
    );
  };

  renderEndDateOptions = (): React.ReactNode => {
    const { endDates } = this.state;

    return endDates.map(({ date, display, paymentsToAdd }) => (
      <option key={date} value={paymentsToAdd} selected={Number(paymentsToAdd) === this.props.numberOfPayments}>
        {display}
      </option>
    ));
  };

  render(): React.ReactNode {
    const { isLoading, monthToMonth, startDate } = this.state;

    const startDateSection = this.renderStartDate();
    const endDateOptions = this.renderEndDateOptions();

    const { paymentEndDateFormatted, showConfirmChangeEndDate, showConfirmMonthToMonth, showConfirmStopPayments } =
      this.state;

    const { rentalProtectionPolicyStatus, listingAlias } = this.props;

    const hasRentalProtectionPolicy =
      rentalProtectionPolicyStatus === ACTIVE || rentalProtectionPolicyStatus === PENDING_ACTIVATION;

    const showEdit = !showConfirmChangeEndDate && !showConfirmMonthToMonth && !showConfirmStopPayments;
    const startDateLabel =
      getDate(coerceToDate(startDate)!) === 1 ? 'First full month of rent' : 'First full rent payment starts';

    return (
      <Modal
        persona={userPersonas.LANDLORD}
        header={
          hasRentalProtectionPolicy && showConfirmStopPayments ? <ZillowInsuranceServicesLogo /> : 'PAYMENT SETTINGS'
        }
        onRequestClose={this.props.onClose}
        dataAutoTestId="landlord-edit-payment-settings"
      >
        {isLoading && <SpinnerContainer />}
        {showConfirmChangeEndDate && (
          <ConfirmChangeEndDate
            paymentEndDateFormatted={paymentEndDateFormatted}
            onConfirm={this.onConfirmChangeEndDate}
            onBack={this.onCloseChangeEndDate}
          />
        )}
        {showConfirmMonthToMonth && (
          <ConfirmMonthToMonth onConfirm={this.onConfirmMonthToMonth} onBack={this.onCloseMonthToMonth} />
        )}
        {showConfirmStopPayments && (
          <ConfirmStopPayments
            onConfirm={this.onConfirmStopPayments}
            onBack={this.onCloseStopPayments}
            hasRentalProtectionPolicy={hasRentalProtectionPolicy}
            listingAlias={listingAlias}
          />
        )}
        {showEdit && (
          <Container>
            <Section>
              <Text type="h3">How long will you be collecting rent?</Text>
            </Section>
            <Section>
              <Label className="flexDisplay">
                {startDateLabel}
                <HelpToolTip>
                  Enter your tenant&apos;s monthly rent payment date. This may be different from the payment date for
                  move-in costs, such as a security deposit.
                </HelpToolTip>
              </Label>
              {startDateSection}
            </Section>
            <Section>
              <Label className="flexDisplay">End date</Label>
              <Select disabled={monthToMonth} onChange={this.onChangeEndDate}>
                {endDateOptions}
              </Select>
            </Section>
            <Section>
              <Checkbox
                checked={monthToMonth}
                onChange={this.onChangeMonthToMonth}
                label="No end date/Month-to-month"
              />
            </Section>
            <Section>
              <DebouncedButton type="primary" onClick={this.onUpdate} full>
                Update
              </DebouncedButton>
            </Section>
            <Or />
            <Section>
              <DebouncedButton type="tertiary" onClick={this.onShowConfirmStopPayments} full>
                Stop payments
              </DebouncedButton>
            </Section>
            <Section>All payments that aren’t already in progress will be stopped immediately.</Section>
            <Section>
              <SpacerRow>
                <FaqComponent.Landlord faqs={FaqConstants.DASHBOARD_V2.EDIT_PAYMENT_LENGTH_PAGE} />
              </SpacerRow>
            </Section>
          </Container>
        )}
      </Modal>
    );
  }
}

export default EditPaymentLengthModal;
